import React, {
  useState,
  useEffect,
  useRef,
  useLayoutEffect,
  useCallback,
} from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, Link } from "react-router-dom";
import {
  settingsService,
  giftVoucherService,
  Settings,
} from "../services/firebase";
import Recaptcha, { RecaptchaRef } from "../components/Recaptcha";
import { recaptchaService } from "../services/recaptchaService";
import PrimaryButton from "components/PrimaryButton";
import "./GiftVoucher.css";

const MAX_VOUCHER_PEOPLE = 8;
const SETTINGS_MAX_RETRIES = 4;
const SETTINGS_RETRY_BASE_MS = 1500;
const SETTINGS_BACKGROUND_RETRY_MS = 5000;
const SETTINGS_CACHE_KEY = "fw_settings_cache_v1";
// How long a cached price is still shown instantly on revisit (revalidated in
// the background regardless). Kept long so the spinner is essentially never seen
// by returning visitors.
const SETTINGS_CACHE_TTL_MS = 24 * 60 * 60 * 1000;
const GIFT_VOUCHER_BG_IMAGE = `${process.env.PUBLIC_URL}/images/gift.png`;

const persistSettingsCache = (settings: Settings): void => {
  try {
    localStorage.setItem(
      SETTINGS_CACHE_KEY,
      JSON.stringify({ data: settings, fetchedAt: Date.now() }),
    );
  } catch {
    // ignore storage errors (private mode, quota)
  }
};

const readCachedVoucherPrice = (): number | null => {
  try {
    const raw = localStorage.getItem(SETTINGS_CACHE_KEY);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw) as {
      data: { voucherPricePerPerson?: number };
      fetchedAt: number;
    };
    if (Date.now() - parsed.fetchedAt >= SETTINGS_CACHE_TTL_MS) {
      return null;
    }
    const price = parsed.data?.voucherPricePerPerson;
    return typeof price === "number" && price > 0 ? price : null;
  } catch {
    return null;
  }
};

const extractVoucherPrice = (settings: Settings): number | null => {
  const price = settings.voucherPricePerPerson;
  return typeof price === "number" && price > 0 ? price : null;
};

const isValidEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

const isValidPhone = (phone: string): boolean => {
  const phoneRegex = /^(\+972|0)([23489]|5[012345689]|77)[0-9]{7}$/;
  const cleanPhone = phone.replace(/[\s-]/g, "");
  return phoneRegex.test(cleanPhone);
};

const GiftVoucher: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const currentLanguage = i18n.language;

  const initialCachedPriceRef = useRef(readCachedVoucherPrice());
  const [pricePerPerson, setPricePerPerson] = useState<number | null>(
    initialCachedPriceRef.current,
  );
  const [priceLoading, setPriceLoading] = useState(
    initialCachedPriceRef.current === null,
  );
  const [priceConfirmedUnavailable, setPriceConfirmedUnavailable] =
    useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [participants, setParticipants] = useState(1);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
  });
  const [recaptchaToken, setRecaptchaToken] = useState<string>("");
  const [recaptchaVerified, setRecaptchaVerified] = useState(false);
  const [consentGiven, setConsentGiven] = useState(false);
  const [showEmailError, setShowEmailError] = useState(false);
  const [showPhoneError, setShowPhoneError] = useState(false);
  const [isFormValid, setIsFormValid] = useState(false);
  const recaptchaRef = useRef<RecaptchaRef>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const fitZoneRef = useRef<HTMLDivElement>(null);
  const fitContentRef = useRef<HTMLDivElement>(null);
  const submitBarRef = useRef<HTMLDivElement>(null);
  const fitRafRef = useRef(0);
  const lastScaleRef = useRef(1);

  const isRtl = currentLanguage === "he";

  const applyFitScale = useCallback(
    (fitContent: HTMLDivElement, scale: number, contentHeight: number) => {
      if (scale < 1) {
        fitContent.style.transform = `scale(${scale})`;
        fitContent.style.transformOrigin = isRtl ? "top right" : "top left";
        fitContent.style.width = `${100 / scale}%`;
        fitContent.style.marginBottom = `${-contentHeight * (1 - scale)}px`;
        return;
      }

      fitContent.style.transform = "none";
      fitContent.style.width = "";
      fitContent.style.marginBottom = "";
    },
    [isRtl],
  );

  const fitGiftVoucherContent = useCallback(() => {
    const fitZone = fitZoneRef.current;
    const fitContent = fitContentRef.current;
    if (!fitZone || !fitContent) {
      return;
    }

    const hadScale = lastScaleRef.current < 1;
    if (hadScale) {
      fitContent.style.transform = "none";
      fitContent.style.width = "";
      fitContent.style.marginBottom = "";
    }

    const availableHeight = fitZone.clientHeight;
    const contentHeight = fitContent.scrollHeight;
    const contentWidth = fitContent.scrollWidth;
    const zoneWidth = fitZone.clientWidth;

    const scaleY =
      contentHeight > 0 && availableHeight > 0
        ? availableHeight / contentHeight
        : 1;
    const scaleX =
      contentWidth > 0 && zoneWidth > 0 ? zoneWidth / contentWidth : 1;
    const scale = Math.min(1, scaleY, scaleX);

    if (Math.abs(scale - lastScaleRef.current) < 0.001) {
      if (hadScale && scale < 1) {
        applyFitScale(fitContent, scale, contentHeight);
      }
      return;
    }

    lastScaleRef.current = scale;
    applyFitScale(fitContent, scale, contentHeight);
  }, [applyFitScale]);

  const scheduleFitGiftVoucherContent = useCallback(() => {
    if (fitRafRef.current) {
      cancelAnimationFrame(fitRafRef.current);
    }

    fitRafRef.current = requestAnimationFrame(() => {
      fitRafRef.current = 0;
      fitGiftVoucherContent();
    });
  }, [fitGiftVoucherContent]);

  useLayoutEffect(() => {
    scheduleFitGiftVoucherContent();

    const container = containerRef.current;
    const fitZone = fitZoneRef.current;
    if (!container || !fitZone) {
      return;
    }

    const observer = new ResizeObserver(() => {
      scheduleFitGiftVoucherContent();
    });

    observer.observe(container);
    observer.observe(fitZone);

    const fitContent = fitContentRef.current;
    const mutationObserver =
      fitContent &&
      new MutationObserver(() => {
        scheduleFitGiftVoucherContent();
      });

    if (mutationObserver && fitContent) {
      mutationObserver.observe(fitContent, {
        childList: true,
        subtree: true,
        attributes: true,
      });
    }

    const handleWindowResize = () => {
      scheduleFitGiftVoucherContent();
    };

    window.addEventListener("resize", handleWindowResize);

    return () => {
      observer.disconnect();
      mutationObserver?.disconnect();
      window.removeEventListener("resize", handleWindowResize);
      if (fitRafRef.current) {
        cancelAnimationFrame(fitRafRef.current);
        fitRafRef.current = 0;
      }
    };
  }, [
    scheduleFitGiftVoucherContent,
    priceLoading,
    pricePerPerson,
    showEmailError,
    showPhoneError,
    participants,
    recaptchaVerified,
    consentGiven,
  ]);

  useEffect(() => {
    window.scrollTo(0, 0);

    let cancelled = false;
    let retryTimerId = 0;

    const clearRetryTimer = () => {
      if (retryTimerId) {
        window.clearTimeout(retryTimerId);
        retryTimerId = 0;
      }
    };

    const scheduleRetry = (attempt: number) => {
      clearRetryTimer();
      const delay =
        attempt < SETTINGS_MAX_RETRIES
          ? SETTINGS_RETRY_BASE_MS * (attempt + 1)
          : SETTINGS_BACKGROUND_RETRY_MS;
      retryTimerId = window.setTimeout(() => {
        if (!cancelled) {
          void loadPrice(attempt);
        }
      }, delay);
    };

    const loadPrice = async (attempt = 0) => {
      try {
        const settings = await settingsService.get();
        if (cancelled) {
          return;
        }

        persistSettingsCache(settings);
        const price = extractVoucherPrice(settings);
        setPricePerPerson(price);
        setPriceConfirmedUnavailable(price === null);
        setPriceLoading(false);
      } catch {
        if (cancelled) {
          return;
        }

        if (initialCachedPriceRef.current !== null) {
          setPriceLoading(false);
          return;
        }

        setPriceConfirmedUnavailable(false);
        setPriceLoading(true);
        scheduleRetry(attempt + 1);
      }
    };

    void loadPrice();

    return () => {
      cancelled = true;
      clearRetryTimer();
    };
  }, []);

  useEffect(() => {
    const emailValid = isValidEmail(formData.email);
    const phoneValid = isValidPhone(formData.phone);
    const nameValid = formData.name.trim().length > 0;
    setIsFormValid(
      emailValid &&
        phoneValid &&
        nameValid &&
        consentGiven &&
        recaptchaVerified,
    );
  }, [formData, consentGiven, recaptchaVerified]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleEmailBlur = () => {
    if (formData.email) {
      setShowEmailError(!isValidEmail(formData.email));
    }
  };

  const handlePhoneBlur = () => {
    if (formData.phone) {
      setShowPhoneError(!isValidPhone(formData.phone));
    }
  };

  const handleParticipantsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const count = parseInt(e.target.value, 10);
    if (count > 0 && count <= MAX_VOUCHER_PEOPLE) {
      setParticipants(count);
    }
  };

  const decreaseParticipants = () => {
    if (participants > 1) {
      setParticipants(participants - 1);
    }
  };

  const increaseParticipants = () => {
    if (participants < MAX_VOUCHER_PEOPLE) {
      setParticipants(participants + 1);
    }
  };

  const handleRecaptchaVerify = (token: string) => {
    setRecaptchaToken(token);
    setRecaptchaVerified(true);
  };

  const handleRecaptchaExpired = () => {
    setRecaptchaToken("");
    setRecaptchaVerified(false);
  };

  const handleRecaptchaError = () => {
    setRecaptchaToken("");
    setRecaptchaVerified(false);
  };

  const totalAmount =
    pricePerPerson !== null
      ? Number((pricePerPerson * participants).toFixed(2))
      : 0;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (pricePerPerson === null) return;

    if (!recaptchaVerified || !recaptchaToken) {
      alert(t("recaptcha"));
      return;
    }
    if (!consentGiven) {
      return;
    }

    setSubmitting(true);
    try {
      const verificationResult = await recaptchaService.verifyToken(
        recaptchaToken,
        "gift_voucher_purchase",
      );
      if (!verificationResult.success) {
        throw new Error(
          `reCAPTCHA verification failed: ${verificationResult.message}`,
        );
      }

      const voucher = await giftVoucherService.createPurchase({
        participants,
        pricePerPerson,
        amountPaid: totalAmount,
        purchaserName: formData.name,
        purchaserEmail: formData.email,
        purchaserPhone: formData.phone,
        language: currentLanguage === "ru" ? "ru" : "he",
      });

      if (!voucher || !voucher.id) {
        throw new Error("Voucher was not created properly");
      }

      if (recaptchaRef.current) {
        recaptchaRef.current.reset();
      }
      setRecaptchaToken("");
      setRecaptchaVerified(false);

      navigate(`/voucher-payment/${voucher.id}`);
    } catch (error) {
      alert(t("errorMessage"));
      setSubmitting(false);
    }
  };

  return (
    <div className="gift-voucher-page">
      <div className="gift-voucher-scene">
        <img
          className="gift-voucher-bg"
          src={GIFT_VOUCHER_BG_IMAGE}
          alt=""
          aria-hidden="true"
        />
        <div
          ref={containerRef}
          className="gift-voucher-container"
          data-language={i18n.language}
          lang={i18n.language === "he" ? "he" : "ru"}
        >
          <div className="gift-voucher-content-outer">
            {!priceLoading && priceConfirmedUnavailable ? (
              <div className="gift-voucher-fit-content">
                <div className="gift-voucher-header">
                  <span className="gift-voucher-emoji" aria-hidden="true">
                    🎁
                  </span>
                  <h1 className="gift-voucher-title">
                    {t("giftVoucherPageTitle")}
                  </h1>
                  <p className="gift-voucher-subtitle">
                    {t("giftVoucherPageSubtitle")}
                  </p>
                  <p className="gift-voucher-intro">
                    {t("giftVoucherPageIntro")}
                  </p>
                </div>
                <div className="gift-voucher-unavailable">
                  {t("giftVoucherNotAvailable")}
                </div>
              </div>
            ) : (
              <form className="gift-voucher-form" onSubmit={handleSubmit}>
                <div ref={fitZoneRef} className="gift-voucher-fit-zone">
                  <div ref={fitContentRef} className="gift-voucher-fit-content">
                    <div className="gift-voucher-header">
                      <span className="gift-voucher-emoji" aria-hidden="true">
                        🎁
                      </span>
                      <h1 className="gift-voucher-title">
                        {t("giftVoucherPageTitle")}
                      </h1>
                      <p className="gift-voucher-subtitle">
                        {t("giftVoucherPageSubtitle")}
                      </p>
                      <p className="gift-voucher-intro">
                        {t("giftVoucherPageIntro")}
                      </p>
                    </div>

                    <div className="gift-voucher-price-banner">
                      <span className="gift-voucher-price-label">
                        {t("giftVoucherPricePerPerson")}
                      </span>
                      <span className="gift-voucher-price-value">
                        {priceLoading || pricePerPerson === null
                          ? t("loading")
                          : `₪${pricePerPerson}`}
                      </span>
                    </div>

                    <div className="gift-voucher-form-group">
                      <input
                        className="gift-voucher-input"
                        type="text"
                        name="name"
                        placeholder={t("namePlaceholder")}
                        value={formData.name}
                        onChange={handleChange}
                        aria-label={t("namePlaceholder")}
                        required
                      />
                    </div>

                    <div className="gift-voucher-form-group">
                      <input
                        className="gift-voucher-input"
                        type="email"
                        name="email"
                        placeholder={t("emailPlaceholder")}
                        value={formData.email}
                        onChange={handleChange}
                        onBlur={handleEmailBlur}
                        aria-label={t("emailPlaceholder")}
                        required
                        aria-invalid={showEmailError}
                        aria-describedby={
                          showEmailError ? "voucher-email-error" : undefined
                        }
                      />
                      {showEmailError && (
                        <small
                          className="gift-voucher-error-text"
                          role="alert"
                          aria-live="polite"
                          id="voucher-email-error"
                        >
                          <span aria-hidden="true">⚠️ </span>
                          {t("invalidEmail")}
                        </small>
                      )}
                    </div>

                    <div className="gift-voucher-form-group">
                      <input
                        className="gift-voucher-input"
                        type="tel"
                        name="phone"
                        placeholder={t("phonePlaceholder")}
                        value={formData.phone}
                        onChange={(e) => {
                          const value = e.target.value.replace(/[^0-9+-]/g, "");
                          setFormData((prev) => ({ ...prev, phone: value }));
                        }}
                        onBlur={handlePhoneBlur}
                        aria-label={t("phonePlaceholder")}
                        required
                      />
                      {showPhoneError && (
                        <small className="gift-voucher-error-text" role="alert">
                          {t("invalidPhone")}
                        </small>
                      )}
                    </div>

                    <div className="gift-voucher-people">
                      <label
                        className="gift-voucher-people-label"
                        htmlFor="voucher-people"
                      >
                        {t("giftVoucherPeopleLabel")}
                      </label>
                      <div className="gift-voucher-people-controls">
                        <button
                          type="button"
                          className="gift-voucher-people-btn"
                          onClick={decreaseParticipants}
                          disabled={participants <= 1}
                          aria-label={t("decreaseTickets")}
                        >
                          −
                        </button>
                        <input
                          id="voucher-people"
                          type="number"
                          min="1"
                          max={MAX_VOUCHER_PEOPLE}
                          value={participants}
                          onChange={handleParticipantsChange}
                          className="gift-voucher-people-input"
                        />
                        <button
                          type="button"
                          className="gift-voucher-people-btn"
                          onClick={increaseParticipants}
                          disabled={participants >= MAX_VOUCHER_PEOPLE}
                          aria-label={t("increaseTickets")}
                        >
                          +
                        </button>
                      </div>
                    </div>

                    <div className="gift-voucher-total">
                      <span className="gift-voucher-total-label">
                        {t("giftVoucherTotal")}
                      </span>
                      <span className="gift-voucher-total-value">
                        ₪{totalAmount}
                      </span>
                    </div>

                    <div className="gift-voucher-form-group">
                      <label className="gift-voucher-checkbox-label">
                        <input
                          type="checkbox"
                          checked={consentGiven}
                          onChange={(e) => setConsentGiven(e.target.checked)}
                          required
                        />
                        <span className="gift-voucher-checkbox-text">
                          {t("consentCheckbox")}{" "}
                          <Link
                            to="/privacy-policy"
                            className="gift-voucher-privacy-link"
                          >
                            {t("privacyPolicy")}
                          </Link>{" "}
                          {t("consentCheckboxDescription")}
                        </span>
                      </label>
                    </div>

                    <div className="gift-voucher-form-group">
                      <label className="gift-voucher-recaptcha-label">
                        {t("recaptcha")}
                      </label>
                      <div className="gift-voucher-recaptcha-wrap">
                        <Recaptcha
                          ref={recaptchaRef}
                          siteKey={
                            process.env.REACT_APP_RECAPTCHA_SITE_KEY ||
                            "6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI"
                          }
                          onVerify={handleRecaptchaVerify}
                          onExpired={handleRecaptchaExpired}
                          onError={handleRecaptchaError}
                          theme="light"
                          size="normal"
                          language={i18n.language}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div ref={submitBarRef} className="gift-voucher-submit-bar">
                  <PrimaryButton
                    fullWidth={true}
                    type="submit"
                    disabled={
                      submitting ||
                      !isFormValid ||
                      priceLoading ||
                      pricePerPerson === null
                    }
                  >
                    {submitting
                      ? t("giftVoucherCreating")
                      : t("giftVoucherPurchaseButton")}
                  </PrimaryButton>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default GiftVoucher;
